import type { ComponentChildren } from "preact";
import { barWidths, breakdownWeights, percentShares } from "./lib/chart.ts";
import { cx } from "./lib/cx.ts";
import { fmtNum } from "./lib/format.ts";

export type ChartTone = "success" | "info" | "neutral" | "muted" | "violet" | "pink" | "cyan" | "danger";
export interface ChartSegment { label: string; value: number; tone: ChartTone }
export interface BarListItem { label: string; value: number; valueLabel: string; href?: string }
export interface BreakdownPart { label: ComponentChildren; value: number; valueLabel: string; emphasis?: boolean }

// See Design > UI, "Curator Studio assets". Small charts state every value in text; color never carries it alone.
export function ProportionBar({ label, segments }: { label: string; segments: ReadonlyArray<ChartSegment> }) {
  const shares = percentShares(segments.map(segment => segment.value));
  return <div class="ui-proportion" role="group" aria-label={label}>
    <div class="ui-proportion__bar" aria-hidden="true">{segments.map((segment, index) => segment.value > 0 && <span key={segment.label} class={`ui-chart-tone--${segment.tone}`} style={{ flex: `${segment.value} 1 0px` }} title={`${segment.label}: ${fmtNum(segment.value)} (${shares[index] ?? 0}%)`} />)}</div>
    <ul class="ui-proportion__legend">{segments.map(segment => <li key={segment.label}><span class={cx("ui-swatch", `ui-chart-tone--${segment.tone}`)} aria-hidden="true" /><span>{segment.label}</span><b>{fmtNum(segment.value)}</b></li>)}</ul>
  </div>;
}
export function BarList({ label, items }: { label: string; items: ReadonlyArray<BarListItem> }) {
  const widths = barWidths(items.map(item => item.value));
  return <ul class="ui-barlist" aria-label={label}>{items.map((item, index) => <li key={item.label}>
    <span class="ui-barlist__label" title={item.label}>{item.href ? <a href={item.href}>{item.label}</a> : item.label}</span>
    <span class="ui-barlist__value">{item.valueLabel}</span>
    <span class="ui-barlist__track" aria-hidden="true"><span style={{ width: `${widths[index] ?? 0}%` }} /></span>
  </li>)}</ul>;
}
export function Breakdown({ label, parts }: { label: string; parts: ReadonlyArray<BreakdownPart> }) {
  const weights = breakdownWeights(parts.map(part => part.value));
  return <div class="ui-breakdown" role="group" aria-label={label}>
    <div class="ui-breakdown__bar" aria-hidden="true">{parts.map((part, index) => <span key={index} data-emphasis={part.emphasis ? "" : undefined} style={{ flex: `${weights[index] ?? 1} 1 0px` }} />)}</div>
    <div class="ui-breakdown__parts">{parts.map((part, index) => <div key={index} data-emphasis={part.emphasis ? "" : undefined}>
      <span class="ui-breakdown__label">{part.label}</span><span class="ui-breakdown__value">{part.valueLabel}</span>
    </div>)}</div>
  </div>;
}

export interface LineSeries {
  name: string; values: ReadonlyArray<number>; tone: ChartTone; total: ComponentChildren;
  /** When present, the legend opens the work this series informs. The caller owns that action. See Design > UI, "Workspace". */
  onSelect?: () => void; actionLabel?: string;
}
/** A small line chart over time: one tone per series, an optional threshold line, and every series' total stated in the legend. */
export function LineChart({ label, series, axis, height = 140, threshold }: {
  label: string; series: ReadonlyArray<LineSeries>; axis: [string, string]; height?: number; threshold?: { value: number; label: string };
}) {
  const max = Math.max(...series.flatMap((s) => [...s.values]), threshold?.value ?? 0, 1e-9) * 1.15;
  const y = (v: number) => height - 8 - (v / max) * (height - 16);
  const points = (v: ReadonlyArray<number>) => v.map((val, i) => `${v.length > 1 ? (i / (v.length - 1)) * 600 : 300},${y(val)}`).join(" ");
  return <figure class="ui-linechart">
    <svg viewBox={`0 0 600 ${height}`} preserveAspectRatio="none" role="img" aria-label={label} style={{ height: `${height}px` }}>
      {[0.25, 0.5, 0.75].map((f) => <line key={f} x1="0" x2="600" y1={y((max / 1.15) * f)} y2={y((max / 1.15) * f)} class="ui-linechart__grid" />)}
      {threshold && <line x1="0" x2="600" y1={y(threshold.value)} y2={y(threshold.value)} class="ui-linechart__threshold" />}
      {series.map((s) => <polyline key={s.name} points={points(s.values)} class={cx("ui-linechart__line", `ui-chart-tone--${s.tone}`)} />)}
    </svg>
    <div class="ui-linechart__axis"><span>{axis[0]}</span>{threshold && <span class="ui-linechart__threshold-label">{threshold.label}</span>}<span>{axis[1]}</span></div>
    <ul class="ui-linechart__legend">{series.map((s) => <li key={s.name}>
      <span class={cx("ui-swatch", `ui-chart-tone--${s.tone}`)} aria-hidden="true" />
      {s.onSelect ? <button type="button" class="ui-linechart__select" onClick={s.onSelect} aria-label={s.actionLabel}><span>{s.name}</span><b>{s.total}</b></button> : <>{s.name}<b>{s.total}</b></>}
    </li>)}</ul>
  </figure>;
}
